import { salesforceTarget, type FlagKind } from '../schemas/flag-type';

import { grantName, orderedGrants } from './grant-provenance-copy';

import type { FlagGrant } from '../schemas/flag';

/**
 * What `OverrideDialog`'s confirm step says (NOXSCRUM-1670).
 *
 * It names the org and the kind of flag. For a `salesforce` flag the value
 * leaves the platform: it is projected into that customer's Salesforce org,
 * one-way, with the audit row here as the only record of why (ADR 0009 §6). For
 * a `veevart2` flag it changes what Veevart 2 services resolve for that museum
 * and nothing more. An operator should not have to remember which one this key
 * is at the moment they agree to it.
 *
 * Pure, like `platform-default-confirm-copy.ts`, so it is testable without a DOM.
 */

export interface OverrideChange {
    readonly tenantId: string;
    readonly enabled: boolean;
    /** Only ever set on a `veevart2` flag — the API refuses one on `salesforce`. */
    readonly rolloutPercentage?: number | undefined;
}

export function overrideConfirmCopy(
    flagKey: string,
    kind: FlagKind,
    change: OverrideChange,
): { readonly title: string; readonly description: string } {
    const value = change.enabled ? 'on' : 'off';

    if (kind.type === 'salesforce') {
        return {
            title: `Project ${flagKey} ${value} to org ${change.tenantId}?`,
            description:
                `${flagKey} is a Salesforce flag. Saving this override projects ${value} ` +
                `into the Salesforce org ${change.tenantId}, as ${salesforceTarget(kind)}, ` +
                `in place of the platform default. No other org changes.`,
        };
    }

    const gate =
        change.rolloutPercentage !== undefined
            ? `, gated at ${String(change.rolloutPercentage)}%`
            : '';

    return {
        title: `Override ${flagKey} for ${change.tenantId}?`,
        description:
            `${flagKey} is a Veevart 2 flag. Veevart 2 services will resolve it ${value}${gate} ` +
            `for ${change.tenantId} in place of the platform default. Nothing is sent to Salesforce.`,
    };
}

/* ------------------------------------------------------------- D15 rule 3 - */

/**
 * The alert before a flag a component granted is turned off.
 *
 * `D15` rule 3, at the only place an operator can still stop: the flags list's
 * own confirmation, which is where the deactivation happens. What they are
 * doing is not "turning one flag off" — it is breaking part of a component this
 * client was sold, and neither the switch nor the row says so.
 *
 * The component is read off the row's **own grants** rather than looked up in
 * the catalog, and that is the load-bearing choice here. `D32` settles that
 * holding a component *is* grant provenance, so the grants on this row are the
 * authoritative answer to which components this museum holds this flag through
 * — and it keeps this feature from importing the catalog, which imports this
 * one. One direction, no cycle, no second source of truth, and no API change.
 *
 * The id is rendered raw, through `grantName`, for the reason
 * `grant-provenance-copy.ts:23-29` gives: there is no catalog on this side to
 * resolve `membership` into a name a human chose, and an invented one is worse
 * than a key because the operator cannot search for it. That limitation is
 * already an open card on the options board; this reuses the wording rather
 * than adding a third of its own.
 *
 * `undefined` when there is nothing to say, so the dialog renders no empty box.
 */
export function componentMembershipAlert(row: {
    readonly enabled: boolean;
    readonly grants: readonly FlagGrant[];
}): string | undefined {
    // Turning it back on takes nothing away, so there is nothing to warn about.
    if (row.enabled) return undefined;

    const components = orderedGrants(row.grants).filter((grant) => grant.kind === 'component');

    if (components.length === 0) return undefined;

    const names = components.map(grantName).join(', ');
    const plural = components.length > 1;

    return (
        `This flag is part of ${names}, which this client holds. Turning it off breaks part of ` +
        `${plural ? 'those components' : 'that component'} for them, and the Components tab ` +
        `will still read as active — the ${plural ? 'grants stay' : 'grant stays'} in place. ` +
        `To withdraw the whole ${plural ? 'entitlement' : 'component'}, deactivate it there instead.`
    );
}
